import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Slide,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Badge,
  Divider,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Chip,
  InputAdornment,
  Tooltip,
  Collapse,
  ListSubheader,
  alpha,
  Fab,
  Zoom,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Drawer,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PushPinIcon from "@mui/icons-material/PushPin";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import FolderIcon from "@mui/icons-material/Folder";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MarkUnreadChatAltIcon from "@mui/icons-material/MarkUnreadChatAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplyIcon from "@mui/icons-material/Reply";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import dayjs from "dayjs";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { getUserInfoFromToken } from "../../utils/Main";
import { baseUrl } from "../../constants";
import axios from "axios";

// Types
type ChannelScope = "WORKSPACE" | "DEPARTMENT" | "PROJECT" | "ACCOUNT";

type Channel = {
  id: number; 
  name: string;
  scope: ChannelScope;
  targetId: number | null;
  createdAt: string;
  updatedAt: string;
};

type Author = {
  id: number;
  username: string;
  profile?: { name?: string | null; imageUrl?: string | null } | null;
};

type ReplyPreview = {
  id: number;
  content: string;
  deletedAt?: string | null;
  author: Author;
};

type ReactionChip = { emoji: string; count: number; reactedByMe: boolean };

type Message = {
  id: number;
  channelId: number;
  content: string;
  replyToId: number | null;
  editedAt: string | null;
  deletedAt?: string | null;
  createdAt: string;
  author: Author;
  replyTo?: ReplyPreview | null;
  reactions?: ReactionChip[];
};

type ListResponse = { items: Message[]; nextCursor: number | null };

// Constants
const TOP_LOAD_THRESHOLD = 120;
const SCROLL_LOAD_COOLDOWN = 350;

type FilterKey = "ALL" | "PINNED" | ChannelScope;

const SCOPES: ChannelScope[] = [
  "WORKSPACE",
  "DEPARTMENT", 
  "PROJECT",
  "ACCOUNT",
];

// Helper functions
function niceScopeLabel(scope: ChannelScope) {
  switch (scope) {
    case "WORKSPACE": return "Workspace";
    case "DEPARTMENT": return "Departments";
    case "PROJECT": return "Projects";
    case "ACCOUNT": return "Accounts";
  }
}

function getScopeColor(scope: ChannelScope, theme: any): string {
  switch (scope) {
    case "WORKSPACE": return theme.palette.primary.main;
    case "DEPARTMENT": return theme.palette.info.main;
    case "PROJECT": return theme.palette.success.main;
    case "ACCOUNT": return theme.palette.warning.main;
  }
}

function getScopeIcon(scope: ChannelScope): JSX.Element {
  switch (scope) {
    case "WORKSPACE": return <WorkspacesIcon fontSize="small" />;
    case "DEPARTMENT": return <CorporateFareIcon fontSize="small" />;
    case "PROJECT": return <FolderIcon fontSize="small" />;
    case "ACCOUNT": return <AccountCircleIcon fontSize="small" />;
  }
}

// Local pin/favorites (no backend)
const PINS_KEY = "chat:pins";
function loadPins(): number[] {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function savePins(ids: number[]) {
  localStorage.setItem(PINS_KEY, JSON.stringify(ids));
}

// Helper functions for chat
function isDivider(
  it: Message | { __divider: string; key: string }
): it is { __divider: string; key: string } {
  return typeof it === "object" && "__divider" in it;
}

function isAtBottom(el: HTMLElement): boolean {
  return el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : text.slice(0, max) + "…";
}

// Day Divider Component
const DayDivider = memo(({ day }: { day: string }) => {
  const theme = useTheme();
  const d = dayjs(day);
  const today = dayjs();
  const yesterday = today.subtract(1, "day");

  let label: string;
  if (d.isSame(today, "day")) {
    label = "Today";
  } else if (d.isSame(yesterday, "day")) {
    label = "Yesterday";
  } else {
    label = d.format("MMMM D, YYYY");
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 1,
        px: 1,
      }}
    >
      <Divider sx={{ flex: 1 }} />
      <Typography
        variant="caption"
        sx={{
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        {label}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
  );
});

// Message Row Component
const MessageRow = memo(({
  m,
  me,
  onMenu,
  onReply,
  onToggleReaction,
  onOpenAddReaction,
}: {
  m: Message;
  me: boolean;
  onMenu: (e: React.MouseEvent<HTMLElement>) => void;
  onReply: () => void;
  onToggleReaction: (m: Message, emoji: string) => void;
  onOpenAddReaction: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
  const theme = useTheme();
  const [showActions, setShowActions] = useState(false);

  if (m.deletedAt) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: me ? "flex-end" : "flex-start",
          py: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.text.secondary, 0.08),
            color: theme.palette.text.secondary,
            fontStyle: "italic",
            border: `1px solid ${alpha(theme.palette.text.secondary, 0.15)}`,
          }}
        >
          This message was deleted
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      sx={{
        display: "flex",
        justifyContent: me ? "flex-end" : "flex-start",
        gap: 1,
        alignItems: "flex-start",
      }}
    >
      {/* Avatar for received messages */}
      {!me && (
        <Avatar
          src={m.author.profile?.imageUrl || undefined}
          sx={{
            width: 32,
            height: 32,
            bgcolor: theme.palette.primary.main,
            fontSize: '0.8rem',
            fontWeight: 600,
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          {m.author.profile?.name ? m.author.profile.name.charAt(0).toUpperCase() : m.author.username.charAt(0).toUpperCase()}
        </Avatar>
      )}

      <Box
        sx={{
          maxWidth: "70%",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {/* Reply Preview */}
        {m.replyTo && (
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderLeft: 3,
              borderColor: theme.palette.primary.main,
            }}
          >
            <Typography variant="caption" sx={{ 
              opacity: 0.9, 
              display: "block",
              color: me ? (theme.palette.mode === 'dark' ? '#ffffff' : '#000000') : theme.palette.text.secondary,
              fontSize: '0.7rem',
              fontWeight: 500,
            }}>
              {m.replyTo.deletedAt
                ? "Replying to deleted message"
                : `Replying to ${m.replyTo.author.profile?.name || m.replyTo.author.username}`}
            </Typography>
            {!m.replyTo.deletedAt && (
              <Typography variant="body2" sx={{ 
                opacity: 0.8, 
                fontSize: "0.8rem",
                color: me ? (theme.palette.mode === 'dark' ? '#ffffff' : '#000000') : theme.palette.text.secondary,
                lineHeight: 1.3,
              }}>
                {truncate(m.replyTo.content, 100)}
              </Typography>
            )}
          </Box>
        )}

        {/* Message Bubble */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: me ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            bgcolor: me 
              ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
              : theme.palette.background.paper,
            color: me ? (theme.palette.mode === 'dark' ? '#ffffff' : '#000000') : theme.palette.text.primary,
            boxShadow: me 
              ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
              : `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
            position: "relative",
            border: 1,
            borderColor: me ? "transparent" : alpha(theme.palette.divider, 0.5),
            maxWidth: '100%',
            wordWrap: 'break-word',
            transition: 'all 0.2s ease-in-out',
            // Ensure theme-aware text for sent messages on blue background
            '& .MuiTypography-root': {
              color: me ? (theme.palette.mode === 'dark' ? '#ffffff !important' : '#000000 !important') : 'inherit',
            },
            '& *': {
              color: me ? (theme.palette.mode === 'dark' ? '#ffffff !important' : '#000000 !important') : 'inherit',
            },
            '&:hover': {
              boxShadow: me 
                ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                : `0 4px 16px ${alpha(theme.palette.common.black, 0.15)}`,
              transform: 'translateY(-1px)',
            },
          }}
        >
          {/* Author name */}
          <Typography
            variant="caption"
            sx={{
              opacity: 0.95,
              display: "block",
              mb: 0.5,
              fontWeight: 600,
              color: me ? (theme.palette.mode === 'dark' ? '#ffffff' : '#000000') : theme.palette.text.primary,
              fontSize: '0.75rem',
            }}
          >
            {m.author.profile?.name || m.author.username}
          </Typography>

          {/* Message content */}
          <Typography 
            variant="body2" 
            sx={{ 
              whiteSpace: "pre-wrap",
              color: me ? (theme.palette.mode === 'dark' ? '#ffffff' : '#000000') : theme.palette.text.primary,
              fontSize: '0.9rem',
              lineHeight: 1.4,
              fontWeight: 400,
            }}
          >
            {m.content}
          </Typography>

          {/* Edit indicator */}
          {m.editedAt && (
            <Typography 
              variant="caption" 
              sx={{ 
                opacity: 0.85, 
                fontSize: "0.7rem",
                color: me ? (theme.palette.mode === 'dark' ? '#ffffff' : '#000000') : theme.palette.text.secondary,
                fontStyle: 'italic',
              }}
            >
              (edited)
            </Typography>
          )}

          {/* Message actions */}
          <Box
            sx={{
              position: "absolute",
              top: -12,
              right: 8,
              display: "flex",
              gap: 0.5,
              opacity: showActions ? 1 : 0,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: showActions ? "translateY(0)" : "translateY(8px)",
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
              borderRadius: '20px',
              p: 0.5,
              boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Tooltip title="Reply" arrow>
              <IconButton
                size="small"
                onClick={onReply}
                sx={{
                  bgcolor: 'transparent',
                  color: theme.palette.text.secondary,
                  "&:hover": { 
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <ReplyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="React" arrow>
              <IconButton
                size="small"
                onClick={onOpenAddReaction}
                sx={{
                  bgcolor: 'transparent',
                  color: theme.palette.text.secondary,
                  "&:hover": { 
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main,
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <AddReactionIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="More" arrow>
              <IconButton
                size="small"
                onClick={onMenu}
                sx={{
                  bgcolor: 'transparent',
                  color: theme.palette.text.secondary,
                  "&:hover": { 
                    bgcolor: alpha(theme.palette.text.secondary, 0.1),
                    color: theme.palette.text.primary,
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Reactions */}
        {m.reactions && m.reactions.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5, justifyContent: me ? 'flex-end' : 'flex-start' }}>
            {m.reactions.map((r, idx) => (
              <Chip
                key={`${r.emoji}-${idx}`}
                size="small"
                label={`${r.emoji} ${r.count}`}
                onClick={() => onToggleReaction(m, r.emoji)}
                sx={{
                  height: 28,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  bgcolor: r.reactedByMe
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`
                    : alpha(theme.palette.text.secondary, 0.06),
                  color: r.reactedByMe
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                  border: r.reactedByMe 
                    ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    : `1px solid ${alpha(theme.palette.text.secondary, 0.15)}`,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  "&:hover": {
                    bgcolor: r.reactedByMe
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
                      : alpha(theme.palette.text.secondary, 0.12),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                  },
                  "&:active": {
                    transform: 'translateY(0)',
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            opacity: 0.6,
            fontSize: "0.7rem",
            alignSelf: me ? "flex-end" : "flex-start",
            color: theme.palette.text.secondary,
          }}
        >
          {dayjs(m.createdAt).format("HH:mm")}
        </Typography>
      </Box>

      {/* Avatar for sent messages */}
      {me && (
        <Avatar
          src={m.author.profile?.imageUrl || undefined}
          sx={{
            width: 32,
            height: 32,
            bgcolor: theme.palette.secondary.main,
            fontSize: '0.8rem',
            fontWeight: 600,
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          {m.author.profile?.name ? m.author.profile.name.charAt(0).toUpperCase() : m.author.username.charAt(0).toUpperCase()}
        </Avatar>
      )}
    </Box>
  );
});

// Edit Overlay Component
function EditOverlay({
  value,
  onChange,
  onSave,
  onCancel,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <Paper
        sx={{
          p: 2,
          minWidth: 300,
          maxWidth: 500,
          width: "90%",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Edit Message
        </Typography>
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          multiline
          rows={3}
          fullWidth
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              onSave();
            } else if (e.key === "Escape") {
              onCancel();
            }
          }}
        />
        <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" onClick={onSave}>
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

// Slide transition for mobile
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Advanced Channel List Component with all features
function AdvancedChannelList({ 
  channels, 
  selectedChannelId, 
  onChannelSelect, 
  loading,
  unreadCounts
}: {
  channels: Channel[];
  selectedChannelId: number | null;
  onChannelSelect: (channelId: number) => void;
  loading: boolean;
  unreadCounts: Record<number, number>;
}) {
  const theme = useTheme();
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey>("ALL");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [pinned, setPinned] = useState<number[]>(() => loadPins());
  const [openSections, setOpenSections] = useState<Record<ChannelScope, boolean>>({
    WORKSPACE: true,
    DEPARTMENT: true,
    PROJECT: true,
    ACCOUNT: true,
  });

  // Filter and group channels
  const qLower = query.trim().toLowerCase();
  const allChannels = useMemo(() => channels ?? [], [channels]);

  // Counts for chips
  const scopeCounts = useMemo(() => {
    const base = allChannels.filter((c) =>
      qLower ? c.name.toLowerCase().includes(qLower) : true
    );
    return {
      ALL: base.length,
      PINNED: base.filter((c) => pinned.includes(c.id)).length,
      WORKSPACE: base.filter((c) => c.scope === "WORKSPACE").length,
      DEPARTMENT: base.filter((c) => c.scope === "DEPARTMENT").length,
      PROJECT: base.filter((c) => c.scope === "PROJECT").length,
      ACCOUNT: base.filter((c) => c.scope === "ACCOUNT").length,
    };
  }, [allChannels, qLower, pinned]);

  // Apply filters
  const channelPassesFilters = (c: Channel) => {
    if (qLower && !c.name.toLowerCase().includes(qLower)) return false;
    if (activeFilter === "PINNED" && !pinned.includes(c.id)) return false;
    if (
      activeFilter !== "ALL" &&
      activeFilter !== "PINNED" &&
      c.scope !== activeFilter
    ) return false;
    if (unreadOnly && (unreadCounts[c.id] || 0) === 0) return false;
    return true;
  };

  const pinnedChannels = useMemo(
    () =>
      allChannels
        .filter((c) => pinned.includes(c.id))
        .filter(channelPassesFilters)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allChannels, pinned, qLower, activeFilter, unreadOnly, unreadCounts]
  );

  const grouped = useMemo(() => {
    const list = allChannels
      .filter((c) => !pinned.includes(c.id))
      .filter(channelPassesFilters);

    const map: Record<ChannelScope, Channel[]> = {
      WORKSPACE: [],
      DEPARTMENT: [],
      PROJECT: [],
      ACCOUNT: [],
    };
    list.forEach((c) => map[c.scope].push(c));
    SCOPES.forEach((s) => map[s].sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [allChannels, pinned, qLower, activeFilter, unreadOnly, unreadCounts]);

  const togglePin = (id: number) => {
    setPinned((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      const arr = Array.from(set);
      savePins(arr);
      return arr;
    });
  };

  const toggleSection = (scope: ChannelScope) => {
    setOpenSections(prev => ({ ...prev, [scope]: !prev[scope] }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} color="primary" />
      </Box>
    );
  }

  if (channels.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography 
          color="text.secondary" 
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          No channels available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: 0, // Important for flexbox scrolling
    }}>
      {/* Search + Filter Section */}
      <Box sx={{ 
        p: 1, 
        borderBottom: 1, 
        borderColor: 'divider',
        flexShrink: 0, // Prevent shrinking
      }}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="small"
          fullWidth
          placeholder="Search chats..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ 
            mb: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.background.paper,
              '& fieldset': {
                borderColor: theme.palette.divider,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
            '& .MuiInputBase-input': {
              color: theme.palette.text.primary,
              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
            },
          }}
        />

        {/* Filter Chips */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            variant={activeFilter === "ALL" ? "filled" : "outlined"}
            color={activeFilter === "ALL" ? "primary" : "default"}
            label={`All (${scopeCounts.ALL})`}
            onClick={() => setActiveFilter("ALL")}
          />
          <Chip
            size="small"
            variant={activeFilter === "PINNED" ? "filled" : "outlined"}
            color={activeFilter === "PINNED" ? "primary" : "default"}
            icon={<PushPinIcon fontSize="small" />}
            label={`Pinned (${scopeCounts.PINNED})`}
            onClick={() => setActiveFilter("PINNED")}
          />
          {SCOPES.map((scope) => (
            <Chip
              key={scope}
              size="small"
              variant={activeFilter === scope ? "filled" : "outlined"}
              sx={{
                ...(activeFilter === scope
                  ? { 
                      bgcolor: alpha(getScopeColor(scope, theme), 0.16), 
                      color: getScopeColor(scope, theme),
                      '& .MuiChip-icon': {
                        color: getScopeColor(scope, theme),
                      }
                    }
                  : { 
                      borderColor: alpha(getScopeColor(scope, theme), 0.4), 
                      color: getScopeColor(scope, theme),
                      '& .MuiChip-icon': {
                        color: getScopeColor(scope, theme),
                      }
                    }),
              }}
              icon={getScopeIcon(scope)}
              label={`${niceScopeLabel(scope)} (${scopeCounts[scope]})`}
              onClick={() => setActiveFilter(scope)}
            />
          ))}
          <Chip
            size="small"
            variant={unreadOnly ? "filled" : "outlined"}
            color={unreadOnly ? "secondary" : "default"}
            icon={<MarkUnreadChatAltIcon fontSize="small" />}
            label="Unread only"
            onClick={() => setUnreadOnly(!unreadOnly)}
          />
        </Box>
      </Box>

      {/* Channels List */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        minHeight: 0, // Important for flexbox scrolling
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.text.secondary, 0.2),
          borderRadius: '3px',
          '&:hover': {
            background: alpha(theme.palette.text.secondary, 0.3),
          },
        },
      }}>
        <List dense sx={{ py: 0 }}>
          {/* Pinned Channels */}
          {(activeFilter === "ALL" || activeFilter === "PINNED") && pinnedChannels.length > 0 && (
            <>
              <ListSubheader sx={{ bgcolor: 'transparent', px: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PushPinIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                  <Typography variant="subtitle2" sx={{ color: theme.palette.warning.main }}>
                    Pinned
                  </Typography>
                  <Chip size="small" label={pinnedChannels.length} />
                </Box>
              </ListSubheader>
              {pinnedChannels.map((channel) => (
                <ChannelRow
                  key={`pinned-${channel.id}`}
                  channel={channel}
                  selected={selectedChannelId === channel.id}
                  unread={unreadCounts[channel.id] || 0}
                  onSelect={() => onChannelSelect(channel.id)}
                  onTogglePin={() => togglePin(channel.id)}
                  pinned={true}
                  theme={theme}
                />
              ))}
              <Divider />
            </>
          )}

          {/* Grouped Channels */}
          {SCOPES.filter(s => activeFilter === "ALL" || activeFilter === s).map((scope) => {
            const list = grouped[scope];
            if (list.length === 0) return null;

            return (
              <React.Fragment key={scope}>
                <ListSubheader 
                  sx={{ 
                    bgcolor: 'transparent', 
                    px: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleSection(scope)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: getScopeColor(scope, theme) }}>
                      {getScopeIcon(scope)}
                    </Box>
                    <Typography variant="subtitle2" sx={{ color: getScopeColor(scope, theme) }}>
                      {niceScopeLabel(scope)}
                    </Typography>
                    <Chip size="small" label={list.length} />
                    <IconButton size="small">
                      {openSections[scope] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </ListSubheader>
                <Collapse in={openSections[scope]}>
                  {list.map((channel) => (
                    <ChannelRow
                      key={channel.id}
                      channel={channel}
                      selected={selectedChannelId === channel.id}
                      unread={unreadCounts[channel.id] || 0}
                      onSelect={() => onChannelSelect(channel.id)}
                      onTogglePin={() => togglePin(channel.id)}
                      pinned={pinned.includes(channel.id)}
                      theme={theme}
                    />
                  ))}
                </Collapse>
                <Divider />
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}

// Individual Channel Row Component
function ChannelRow({
  channel,
  selected,
  unread,
  onSelect,
  onTogglePin,
  pinned,
  theme
}: {
  channel: Channel;
  selected: boolean;
  unread: number;
  onSelect: () => void;
  onTogglePin: () => void;
  pinned: boolean;
  theme: any;
}) {
  const color = getScopeColor(channel.scope, theme);

  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{
        alignItems: 'center',
        '&.Mui-selected': {
          bgcolor: alpha(color, 0.08),
        },
        '&:hover': { bgcolor: alpha(color, 0.06) },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: alpha(color, 0.15),
          color,
          fontSize: 14,
        }}
      >
        {getScopeIcon(channel.scope)}
      </Avatar>
      <ListItemText
        primary={channel.name}
        secondary={niceScopeLabel(channel.scope)}
        primaryTypographyProps={{ 
          fontSize: '0.9rem',
        }}
        secondaryTypographyProps={{ 
          fontSize: '0.75rem',
          sx: { opacity: 0.7 }
        }}
        sx={{ ml: 1 }}
      />
      <Badge
        color="primary"
        badgeContent={unread}
        max={99}
        invisible={unread === 0}
        sx={{ mr: 0.5 }}
      />
      <Tooltip title={pinned ? "Unpin" : "Pin"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          edge="end"
          aria-label={pinned ? "Unpin channel" : "Pin channel"}
          sx={{ ml: 0.5 }}
        >
          {pinned ? (
            <StarRoundedIcon fontSize="small" color="warning" />
          ) : (
            <StarBorderRoundedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
}

// Advanced Chat Window Component with all features
function AdvancedChatWindow({ 
  channelId, 
  currentUserId, 
  token 
}: {
  channelId: number | null;
  currentUserId: number;
  token: string;
}) {
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [channelName, setChannelName] = useState<string>("Channel");
  const [atBottom, setAtBottom] = useState(true);
  
  // Message actions
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuMsgId, setMenuMsgId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>("");
  
  // Reply
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  
  // Emoji
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [reactionAnchor, setReactionAnchor] = useState<HTMLElement | null>(null);
  const [reactionForMsg, setReactionForMsg] = useState<number | null>(null);
  
  // Toast
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error" | "info";
  }>({ open: false, msg: "", severity: "info" });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const initialAutoScrolledRef = useRef(false);
  const lastLoadTickRef = useRef(0);
  const lastRequestedCursorRef = useRef<number | null>(null);

  // Load channel name
  useEffect(() => {
    if (!channelId) return;
    axios
      .get<{ id: number; name: string }[]>(`${baseUrl}/chat/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const ch = (res.data || []).find((c) => c.id === channelId);
        setChannelName(ch?.name ?? "Channel");
      })
      .catch(() => setChannelName("Channel"));
  }, [channelId, token]);

  // Load messages
  useEffect(() => {
    if (!channelId) return;

    setMessages([]);
    setNextCursor(null);
    setHasMore(false);
    setLoading(true);
    initialAutoScrolledRef.current = false;
    lastRequestedCursorRef.current = null;
    // Clear typing users when channel changes
    setReplyTo(null);

    axios
      .get<ListResponse>(`${baseUrl}/chat/channels/${channelId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 30 },
      })
      .then((res) => {
        setMessages(res.data.items);
        setNextCursor(res.data.nextCursor);
        setHasMore(!!res.data.nextCursor);
        
        // Mark as read
        axios.post(
          `${baseUrl}/chat/channels/${channelId}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        requestAnimationFrame(() => {
          bottomRef.current?.scrollIntoView();
          initialAutoScrolledRef.current = true;
        });
      })
      .finally(() => setLoading(false));
  }, [channelId, token]);

  // Load older messages
  const loadOlder = useCallback(async () => {
    if (!hasMore || loadingOlder || !nextCursor || !channelId) return;
    if (lastRequestedCursorRef.current === nextCursor) return;
    lastRequestedCursorRef.current = nextCursor;

    const el = containerRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;

    setLoadingOlder(true);
    try {
      const res = await axios.get<ListResponse>(
        `${baseUrl}/chat/channels/${channelId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { cursor: nextCursor, limit: 30 },
        }
      );
      setMessages((prev) => [...res.data.items, ...prev]);
      setNextCursor(res.data.nextCursor);
      setHasMore(!!res.data.nextCursor);

      // Maintain scroll position after loading older messages
      requestAnimationFrame(() => {
        const newHeight = el?.scrollHeight ?? 0;
        if (el) el.scrollTop = prevTop + (newHeight - prevHeight);
      });
    } catch (error) {
      console.error('Error loading older messages:', error);
      setToast({ open: true, msg: "Failed to load older messages", severity: "error" });
    } finally {
      setLoadingOlder(false);
    }
  }, [hasMore, loadingOlder, nextCursor, channelId, token]);

  // Scroll handler
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setAtBottom(isAtBottom(el));
    const now = Date.now();
    if (
      hasMore &&
      !loadingOlder &&
      now - lastLoadTickRef.current > SCROLL_LOAD_COOLDOWN &&
      el.scrollTop <= TOP_LOAD_THRESHOLD
    ) {
      lastLoadTickRef.current = now;
      void loadOlder();
    }
  }, [hasMore, loadingOlder, loadOlder]);

  // Send message
  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || !channelId) return;
    const payload: any = { content: trimmed };
    if (replyTo) payload.replyToId = replyTo.id;

    setInput("");
    setReplyTo(null);

    try {
      const { data } = await axios.post<Message>(
        `${baseUrl}/chat/channels/${channelId}/messages`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, data]);
      requestAnimationFrame(() =>
        bottomRef.current?.scrollIntoView({ behavior: "smooth" })
      );
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [input, replyTo, channelId, token]);

  // Message actions
  const openMenu = (e: React.MouseEvent<HTMLElement>, msgId: number) => {
    setMenuAnchor(e.currentTarget);
    setMenuMsgId(msgId);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuMsgId(null);
  };

  const startEdit = (msg: Message) => {
    setEditingId(msg.id);
    setEditingText(msg.content);
    closeMenu();
  };

  const saveEdit = async () => {
    if (!editingId || !channelId) return;
    const trimmed = editingText.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    try {
      await axios.post(
        `${baseUrl}/chat/messages/${editingId}/edit`,
        { content: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => 
        prev.map(m => m.id === editingId ? { ...m, content: trimmed, editedAt: new Date().toISOString() } : m)
      );
    } catch (error) {
      console.error('Error editing message:', error);
    } finally {
      setEditingId(null);
      setEditingText("");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const deleteMsg = async (id: number) => {
    closeMenu();
    const ok = window.confirm("Delete this message?");
    if (!ok) return;
    try {
      await axios.post(
        `${baseUrl}/chat/messages/${id}/delete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => prev.map(m => m.id === id ? { ...m, deletedAt: new Date().toISOString() } : m));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const copyMsg = async (msg: Message) => {
    try {
      await navigator.clipboard.writeText(msg.content || "");
      setToast({ open: true, msg: "Copied", severity: "success" });
    } catch {
      setToast({ open: true, msg: "Copy failed", severity: "error" });
    }
    closeMenu();
  };

  // Emoji functions
  const openEmoji = (e: React.MouseEvent<HTMLElement>) =>
    setEmojiAnchor(e.currentTarget as HTMLElement);
  const closeEmoji = () => setEmojiAnchor(null);
  const onEmojiSelect = (emoji: any) => {
    const toAdd = emoji?.native ?? emoji?.shortcodes ?? "";
    setInput((prev) => `${prev}${toAdd}`);
  };

  // Reply functions
  const beginReply = (msg: Message) => setReplyTo(msg);
  const cancelReplyPreview = () => setReplyTo(null);

  // Reactions
  const toggleReaction = async (m: Message, emoji: string) => {
    const already = (m.reactions || []).find(
      (r) => r.emoji === emoji && r.reactedByMe
    );
    try {
      if (already) {
        await axios.delete(
          `${baseUrl}/chat/messages/${m.id}/reactions/${encodeURIComponent(emoji)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Optimistically update UI
        setMessages(prev => prev.map(msg => 
          msg.id === m.id 
            ? {
                ...msg,
                reactions: (msg.reactions || []).map(r => 
                  r.emoji === emoji && r.reactedByMe
                    ? { ...r, count: Math.max(0, r.count - 1), reactedByMe: false }
                    : r
                ).filter(r => r.count > 0)
              }
            : msg
        ));
      } else {
        await axios.post(
          `${baseUrl}/chat/messages/${m.id}/reactions`,
          { emoji },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Optimistically update UI
        setMessages(prev => prev.map(msg => 
          msg.id === m.id 
            ? {
                ...msg,
                reactions: [
                  ...(msg.reactions || []).filter(r => r.emoji !== emoji),
                  {
                    emoji,
                    count: ((msg.reactions || []).find(r => r.emoji === emoji)?.count || 0) + 1,
                    reactedByMe: true
                  }
                ]
              }
            : msg
        ));
      }
    } catch (error) {
      console.error('Error toggling reaction:', error);
      setToast({ open: true, msg: "Failed to add reaction", severity: "error" });
    }
  };

  const openAddReaction = (e: React.MouseEvent<HTMLElement>, msgId: number) => {
    setReactionAnchor(e.currentTarget);
    setReactionForMsg(msgId);
  };

  const closeAddReaction = () => {
    setReactionAnchor(null);
    setReactionForMsg(null);
  };

  const onSelectReaction = (emojiObj: any) => {
    const emoji = emojiObj?.native ?? emojiObj?.shortcodes ?? "";
    if (emoji && reactionForMsg) {
      const m = messages.find((x) => x.id === reactionForMsg);
      if (m) toggleReaction(m, emoji);
    }
    closeAddReaction();
  };

  // Input change with typing indicator
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    // In a real app, you'd emit typing events here
  };

  // Day dividers
  const withDividers: (Message | { __divider: string; key: string })[] =
    useMemo(() => {
      const out: (Message | { __divider: string; key: string })[] = [];
      let lastDay: string | null = null;
      messages.forEach((m) => {
        const day = dayjs(m.createdAt).format("YYYY-MM-DD");
        if (day !== lastDay) {
          out.push({ __divider: day, key: `div-${day}-${m.id}` });
          lastDay = day;
        }
        out.push(m);
      });
      return out;
    }, [messages]);

  if (!channelId) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%',
        flexDirection: 'column',
        gap: 2,
        color: 'text.secondary',
        p: 3,
        background: `linear-gradient(135deg, ${
          theme.palette.mode === "light" 
            ? `${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%`
            : `${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%`
        })`,
      }}>
        <Box sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 64, opacity: 0.6, color: theme.palette.primary.main }} />
          <Typography 
            variant="h5" 
            fontWeight={600} 
            sx={{ color: theme.palette.primary.main }}
          >
            Welcome to Messenger
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.palette.text.secondary,
              textAlign: "center"
            }}
          >
            Select a channel from the sidebar to start chatting with your team
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      minHeight: 0, // Important for flexbox scrolling
    }}>
      {/* Header */}
      <Box
        sx={{
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: 'background.paper',
          flexShrink: 0, // Prevent shrinking
        }}
      >
        <Typography 
          variant="subtitle1" 
          noWrap 
          fontWeight={700}
          sx={{ color: theme.palette.text.primary }}
        >
          {channelName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasMore && (
              <Button
                size="small"
                onClick={loadOlder}
                disabled={loadingOlder}
                variant="outlined"
                sx={{ color: theme.palette.text.primary }}
              >
                {loadingOlder ? "Loading…" : "Load older"}
              </Button>
          )}
        </Box>
      </Box>

      {/* Messages Area */}
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          minHeight: 0, // Important for flexbox scrolling
          background: `linear-gradient(135deg, ${
            theme.palette.mode === "light" 
              ? `${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%`
              : `${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%`
          })`,
          position: 'relative',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.text.secondary, 0.3),
            borderRadius: '4px',
            '&:hover': {
              background: alpha(theme.palette.text.secondary, 0.5),
            },
          },
        }}
      >
        {loadingOlder && (
          <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={16} />
          </Box>
        )}

        {loading && !messages.length ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            gap: 2,
            color: theme.palette.text.secondary
          }}>
            <CircularProgress size={40} color="primary" />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Loading messages…
            </Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            gap: 2,
            color: theme.palette.text.secondary
          }}>
            <ChatBubbleOutlineIcon sx={{ 
              fontSize: 64, 
              opacity: 0.3,
              color: theme.palette.text.secondary 
            }} />
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
              No messages yet
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Start the conversation by sending a message
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {withDividers.map((it) =>
              isDivider(it) ? (
                <DayDivider key={it.key} day={it.__divider} />
              ) : (
                <MessageRow
                  key={it.id}
                  m={it}
                  me={it.author.id === currentUserId}
                  onMenu={(e) => openMenu(e, it.id)}
                  onReply={() => beginReply(it)}
                  onToggleReaction={toggleReaction}
                  onOpenAddReaction={(e) => openAddReaction(e, it.id)}
                />
              )
            )}
            <div ref={bottomRef} />
          </Stack>
        )}

        {/* Scroll-to-bottom FAB */}
        <Zoom in={!atBottom}>
          <Fab
            size="small"
            color="primary"
            onClick={() =>
              bottomRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            sx={{ position: "absolute", right: 16, bottom: 16 }}
            aria-label="Scroll to latest"
          >
            <KeyboardArrowDownIcon />
          </Fab>
        </Zoom>
      </Box>

      <Divider />

      {/* Composer */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1.5,
        flexShrink: 0, // Prevent shrinking
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.5)} 100%)`,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      }}>
        {replyTo && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <ReplyIcon fontSize="small" color="primary" />
              <Typography variant="caption" sx={{ 
                opacity: 0.9, 
                fontWeight: 500,
                color: theme.palette.text.primary,
              }} noWrap>
                Replying to{" "}
                <b style={{ color: theme.palette.primary.main }}>
                  {replyTo.author.profile?.name || replyTo.author.username}
                </b>
                :&nbsp;
                <i style={{ 
                  opacity: 0.7,
                  color: theme.palette.text.secondary,
                }}>{truncate(replyTo.content, 80)}</i>
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={cancelReplyPreview} 
              aria-label="Cancel reply"
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': { 
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                }
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          alignItems: 'flex-end',
          p: 1,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          backdropFilter: 'blur(10px)',
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
        }}>
          <Tooltip title="Add emoji" arrow>
            <IconButton 
              onClick={openEmoji} 
              size="small" 
              aria-label="Insert emoji"
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': { 
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                }
              }}
            >
              <EmojiEmotionsOutlinedIcon />
            </IconButton>
          </Tooltip>

          <TextField
            value={input}
            onChange={onInputChange}
            placeholder="Write a message…"
            fullWidth
            size="small"
            multiline
            maxRows={6}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '0.95rem',
                '& .MuiInputBase-input': {
                  py: 1,
                  color: theme.palette.text.primary,
                  fontWeight: 400,
                  lineHeight: 1.4,
                  '&::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 0.8,
                    fontWeight: 400,
                  },
                }
              }
            }}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                handleSend();
              } else if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              } else if (e.key === "Escape") {
                if (replyTo) cancelReplyPreview();
              }
            }}
          />

          <Tooltip title="Send (Enter)" arrow>
            <IconButton 
              onClick={handleSend} 
              aria-label="Send message" 
              disabled={!input.trim()}
              sx={{
                bgcolor: input.trim() 
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : alpha(theme.palette.text.secondary, 0.2),
                color: input.trim() 
                  ? theme.palette.primary.contrastText 
                  : theme.palette.text.secondary,
                boxShadow: input.trim() 
                  ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                  : 'none',
                '&:hover': {
                  bgcolor: input.trim() 
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                    : alpha(theme.palette.text.secondary, 0.3),
                  transform: input.trim() ? 'translateY(-1px)' : 'none',
                  boxShadow: input.trim() 
                    ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
                    : 'none',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Message action menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            const msg = messages.find((m) => m.id === menuMsgId);
            if (msg) startEdit(msg);
          }}
          disabled={
            !menuMsgId ||
            messages.find((m) => m.id === menuMsgId)?.author.id !== currentUserId
          }
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => menuMsgId && deleteMsg(menuMsgId)}
          disabled={
            !menuMsgId ||
            messages.find((m) => m.id === menuMsgId)?.author.id !== currentUserId
          }
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Delete
        </MenuItem>
        <MenuItem
          onClick={() => {
            const msg = messages.find((m) => m.id === menuMsgId);
            if (msg) copyMsg(msg);
          }}
          disabled={!menuMsgId}
        >
          <ContentCopyIcon fontSize="small" style={{ marginRight: 8 }} /> Copy
        </MenuItem>
        <MenuItem
          onClick={() => {
            const msg = messages.find((m) => m.id === menuMsgId);
            if (msg) setReplyTo(msg);
            closeMenu();
          }}
          disabled={!menuMsgId}
        >
          <ReplyIcon fontSize="small" style={{ marginRight: 8 }} /> Reply
        </MenuItem>
      </Menu>

      {/* Emoji picker (composer) */}
      <Popover
        open={!!emojiAnchor}
        anchorEl={emojiAnchor}
        onClose={closeEmoji}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { boxShadow: 3 } }}
      >
        <Box sx={{ p: 1 }}>
          <Picker data={data} onEmojiSelect={onEmojiSelect} theme="auto" />
        </Box>
      </Popover>

      {/* Add reaction picker */}
      <Popover
        open={!!reactionAnchor}
        anchorEl={reactionAnchor}
        onClose={closeAddReaction}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{ sx: { boxShadow: 3 } }}
      >
        <Box sx={{ p: 1 }}>
          <Picker data={data} onEmojiSelect={onSelectReaction} theme="auto" />
        </Box>
      </Popover>

      {/* Inline edit overlay */}
      {editingId && (
        <EditOverlay
          value={editingText}
          onChange={setEditingText}
          onSave={saveEdit}
          onCancel={cancelEdit}
        />
      )}

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((p) => ({ ...p, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

interface ChatDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ChatDialog({ open, onClose }: ChatDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Load dialog position from localStorage or default to 'center'
  const [dialogPosition, setDialogPosition] = useState<'right' | 'center' | 'full'>(() => {
    try {
      const saved = localStorage.getItem('messenger_dialog_position');
      if (saved && ['right', 'center', 'full'].includes(saved)) {
        return saved as 'right' | 'center' | 'full';
      }
    } catch (error) {
      console.warn('Failed to load messenger dialog position from localStorage:', error);
    }
    return 'center';
  });
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(true);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<number, number>>({});

  // Save dialog position to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('messenger_dialog_position', dialogPosition);
    } catch (error) {
      console.warn('Failed to save messenger dialog position to localStorage:', error);
    }
  }, [dialogPosition]);

  const auth = useMemo(() => getUserInfoFromToken(), []);
  const userId: number | undefined = auth?.id;

  // Auto-open drawer when switching to right panel mode
  useEffect(() => {
    if (dialogPosition === 'right') {
      setLeftDrawerOpen(true);
    }
  }, [dialogPosition]);

  // Auto-open drawer when dialog opens
  useEffect(() => {
    if (open) {
      setLeftDrawerOpen(true);
    }
  }, [open]);

  const token: string | undefined = useMemo(
    () => localStorage.getItem("accessToken") ?? localStorage.getItem("token") ?? undefined,
    []
  );

  // Load channels when dialog opens
  useEffect(() => {
    if (!open || !token) return;

    setChannelsLoading(true);
    Promise.all([
      // Load channels
      axios.get(`${baseUrl}/chat/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      // Load unread counts
      axios.get(`${baseUrl}/chat/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => ({ data: {} })) // Ignore errors for unread counts
    ])
    .then(([channelsResponse, unreadResponse]) => {
      setChannels(channelsResponse.data || []);
      setUnreadCounts(unreadResponse.data || {});
    })
    .catch((error) => {
      console.error('Error loading chat data:', error);
      setChannels([]);
      setUnreadCounts({});
    })
    .finally(() => {
      setChannelsLoading(false);
    });
  }, [open, token]);

  // Reset selection when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedChannelId(null);
    }
  }, [open]);

  // Clear unread count for selected channel
  const handleChannelSelect = (channelId: number) => {
    setSelectedChannelId(channelId);
    // Clear unread count for this channel
    setUnreadCounts(prev => ({
      ...prev,
      [channelId]: 0
    }));
    
    // Mark as read on server
    if (token) {
      axios.post(
        `${baseUrl}/chat/channels/${channelId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(console.error);
    }
    
    // Auto-close drawer when in right panel mode
    if (dialogPosition === 'right') {
      setLeftDrawerOpen(false);
    }
  };

  if (!userId || !token) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Transition : undefined}
      >
        <DialogContent>
          <Typography color="error">You must be logged in to use chat.</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={dialogPosition === 'full' ? false : dialogPosition === 'right' ? 'sm' : 'lg'}
      fullWidth={dialogPosition !== 'full'}
      fullScreen={isMobile || dialogPosition === 'full'}
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{
        sx: {
          borderRadius: isMobile || dialogPosition === 'full' ? 0 : 2,
          height: isMobile || dialogPosition === 'full' ? '100vh' : '85vh',
          maxHeight: isMobile || dialogPosition === 'full' ? '100vh' : '85vh',
          margin: isMobile || dialogPosition === 'full' ? 0 : 2,
          display: 'flex',
          flexDirection: 'column',
          ...(dialogPosition === 'right' && {
            position: 'fixed',
            right: 0,
            top: 0,
            bottom: 0,
            width: '400px',
            maxWidth: '400px',
            height: '100vh',
            maxHeight: '100vh',
            margin: 0,
            borderRadius: 0,
          }),
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}10 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="h6" 
            fontWeight={700}
            sx={{ color: theme.palette.text.primary }}
          >
            Messenger
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Team Chat
          </Box>
        </Box>
        
        {/* Left Drawer Toggle Button */}
        {!isMobile && (
          <Tooltip title={leftDrawerOpen ? "Hide Channel List" : "Show Channel List"} arrow>
            <IconButton
              onClick={() => setLeftDrawerOpen(!leftDrawerOpen)}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                },
              }}
            >
              {leftDrawerOpen ? <ChevronLeftIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        {/* Dialog Position Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={dialogPosition}
            exclusive
            onChange={(_, value) => value && setDialogPosition(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                },
              },
            }}
          >
            <ToggleButton value="right" aria-label="Right panel">
              <Tooltip title="Right Panel" arrow>
                <ViewSidebarIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="center" aria-label="Center dialog">
              <Tooltip title="Center Dialog" arrow>
                <CenterFocusStrongIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="full" aria-label="Full page">
              <Tooltip title="Full Page" arrow>
                <FullscreenIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          height: '100%',
          flexDirection: isMobile ? 'column' : 'row',
          minHeight: 0, // Important for flexbox scrolling
        }}>
          {/* Left Side - Collapsible Channel List Drawer */}
          <Drawer
            variant="persistent"
            anchor="left"
            open={leftDrawerOpen && !isMobile}
            sx={{
              width: leftDrawerOpen ? 350 : 0,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 350,
                boxSizing: 'border-box',
                position: 'relative',
                height: '100%',
                border: 'none',
                borderRight: `1px solid ${theme.palette.divider}`,
                transition: 'width 0.3s ease-in-out',
                overflow: 'hidden',
              },
            }}
          >
            <Paper
              variant="outlined"
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 0,
                border: 'none',
              }}
            >
              <AdvancedChannelList
                channels={channels}
                selectedChannelId={selectedChannelId}
                onChannelSelect={handleChannelSelect}
                loading={channelsLoading}
                unreadCounts={unreadCounts}
              />
            </Paper>
          </Drawer>

          {/* Mobile Channel List */}
          {isMobile && (
            <Box sx={{ 
              width: '100%',
              height: '40%',
              minHeight: '200px',
              maxHeight: '300px',
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              overflow: 'hidden',
            }}>
              <Paper
                variant="outlined"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 0,
                  border: 'none',
                }}
              >
                <AdvancedChannelList
                  channels={channels}
                  selectedChannelId={selectedChannelId}
                  onChannelSelect={handleChannelSelect}
                  loading={channelsLoading}
                  unreadCounts={unreadCounts}
                />
              </Paper>
            </Box>
          )}

          {/* Right Side - Chat Window */}
          <Box sx={{ 
            flex: 1,
            height: isMobile ? '60%' : '100%',
            minHeight: isMobile ? '300px' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <AdvancedChatWindow
              channelId={selectedChannelId}
              currentUserId={userId}
              token={token}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
